import { createSlice } from '@reduxjs/toolkit';
import socket from '../../utils/socket';

// Etat initial du slice
const initialState = {
  preferences: null, // Stocke les préférences de l'utilisateur
  isLoading: false, // Indique si une requête est en cours
  error: null // Stocke un message d'erreur en cas de problème
};

// Création du slice pour les préférences utilisateur
const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    /**
     * Active l'état de chargement (`isLoading = true`).
     */
    startLoading: (state) => {
      state.isLoading = true;
    },

    /**
     * Enregistre un message d'erreur et désactive l'état de chargement.
     * @param {Object} state - État actuel
     * @param {Object} action - Action contenant `payload` avec le message d'erreur
     */
    hasError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    /**
     * Met à jour les préférences de l'utilisateur dans le store Redux.
     * @param {Object} state - État actuel
     * @param {Object} action - Action contenant `payload` avec les nouvelles préférences
     */
    setPreferences: (state, action) => {
      state.preferences = { ...state.preferences, ...action.payload }; // Fusionner sans écraser tout l'état
      state.isLoading = false;
      state.error = null;
    }
  }
});

// Export du reducer
export default preferencesSlice.reducer;

// Export des actions
export const { startLoading, hasError, setPreferences } = preferencesSlice.actions;

/**
 * Action asynchrone pour récupérer ou mettre à jour les préférences d'un utilisateur.
 *
 * @param {number} userId - Identifiant de l'utilisateur
 * @param {Object|null} newData - Données à mettre à jour (facultatif)
 * @returns {Function} Fonction dispatchable pour Redux Thunk
 */
export function updatePreferences(userId, newData = null) {
  return async (dispatch) => {
    dispatch(startLoading());

    try {
      let response;

      if (newData) {
        // Mise à jour des préférences utilisateur en base
        response = await socket.put(`/preferences/${userId}`, newData);
      } else {
        // Récupération des préférences depuis l'API
        response = await socket.get(`/preferences/${userId}`);
      }

      // Vérification de la réponse de l'API
      if (response.success) {
        // Mise à jour des préférences dans Redux (on ne stocke que `response.data`)
        dispatch(setPreferences(response));
      } else {
        // Gestion d'une erreur retournée par l'API
        dispatch(hasError(response.message || 'Erreur inconnue'));
      }
    } catch (error) {
      // Gestion des erreurs côté client
      dispatch(hasError(error.message || 'Erreur inconnue'));
    }
  };
}
